import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { auditTrailsMockData } from '@/lib/constants';
import TicketModal from 'components/tickets/TicketModal';

interface AuditTrailsProps {
  filters: {
    userName: string;
    userType: string;
    change: string;
    date: string;
    search: string;
    organization: string;
    startDate: Date | null;
    endDate: Date | null;
  };
  setFilters: (filters: any) => void;
}

export default function AuditTrails({ filters, setFilters }: AuditTrailsProps) {
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [selectedLogForTicket, setSelectedLogForTicket] = useState<any | null>(null);

  const filteredLogs = auditTrailsMockData.filter(log => {
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      const searchMatch =
        log.user.toLowerCase().includes(searchTerm) ||
        log.userType.toLowerCase().includes(searchTerm) ||
        log.change.toLowerCase().includes(searchTerm) ||
        log.description.toLowerCase().includes(searchTerm) ||
        log.date.toLowerCase().includes(searchTerm);

      if (!searchMatch) return false;
    }

    // Other filters
    if (filters.userName && !log.user.toLowerCase().includes(filters.userName.toLowerCase())) return false;
    if (filters.userType && log.userType !== filters.userType) return false;
    if (filters.change && log.change !== filters.change) return false;
    if (filters.date && !log.date.includes(filters.date)) return false;
    if (filters.organization && !log.organization.toLowerCase().includes(filters.organization.toLowerCase())) return false;

    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const logDate = new Date(log.date);
      // Set time to start of day for start date and end of day for end date
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);

      if (logDate < startDate || logDate > endDate) return false;
    }

    return true;
  });

  const DetailRow = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
    <div className={`flex border-b border-white/[0.08] py-3 ${className}`}>
      <div className="w-1/4 text-gray-400">{label}</div>
      <div className="w-2/4 text-white break-words whitespace-pre-wrap">{value}</div>
    </div>
  );

  const handleReport = (log: any) => {
    setSelectedLogForTicket(log);
    setIsTicketModalOpen(true);
  };

  return (
    <>

      <div className="sticky top-[130px] bg-black z-10 pt-4">
        <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
          <table className="w-full table-fixed">
            <thead>
            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
              <th className="text-left p-4">Dates</th>
              <th className="text-left p-4">Organization</th>
              <th className="text-left p-4">User Name</th>
              <th className="text-left p-4">User Type</th>
              <th className="text-left p-4">Change</th>
              <th className="text-left p-4">Description</th>
            </tr>
            </thead>
          </table>
        </div>
      </div>

      <div className="bg-[#2A2A2A] rounded-lg overflow-hidden">
        <table className="w-full table-fixed">
          <tbody>
            {filteredLogs.map((log, index) => (
              <React.Fragment key={index}>
                <tr
                  className="border-b border-white/[0.08] hover:bg-[#3a3a3a33] cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {expandedLog === index ? (
                        <FiChevronDown className="text-gray-400" />
                      ) : (
                        <FiChevronRight className="text-gray-400" />
                      )}
                      <span className="text-gray-400">{new Date(log.date).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-400">{log.organization}</td>
                  <td className="p-4">
                    <span className="text-white">{log.user}</span>
                  </td>
                  <td className="p-4 text-gray-400">{log.userType}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${log.change.toLowerCase().includes('update') ? 'bg-blue-900/30 text-blue-400' :
                        log.change.toLowerCase().includes('delete') ? 'bg-[#e11d486b] text-red-500' :
                          'bg-[#1C3829] text-[#4ADE80]'
                      }`}>
                      {log.change}
                    </span>
                  </td>
                  <td className="p-4 text-gray-400 break-words whitespace-pre-wrap">{log.description}</td>
                </tr>

                {expandedLog === index && (
                  <tr>
                    <td colSpan={6} className="bg-[#1a1a1a] border-b border-gray-800/50 w-full">
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-lg text-white font-medium">System Logs Details</h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReport(log);
                            }}
                            className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
                          >
                            Report
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <DetailRow label="Date" value={new Date(log.date).toLocaleDateString()} />
                            <DetailRow label="Organization" value={log.organization} />
                            <DetailRow label="User" value={log.user} />
                            <DetailRow label="User Type" value={log.userType} />
                          </div>
                          <div className="space-y-3">
                            <DetailRow label="Change" value={log.change} />
                            <DetailRow label="Description" value={log.description} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <TicketModal
        isOpen={isTicketModalOpen}
        onClose={() => {
          setIsTicketModalOpen(false);
          setSelectedLogForTicket(null);
        }}
        mode="create"
        initialData={selectedLogForTicket ? {
          id: '',
          status: 'open',
          customer: selectedLogForTicket.user,
          name: `Report: Audit Issue - ${selectedLogForTicket.change}`,
          description: `Reporting audit issue:\n\nDate: ${selectedLogForTicket.date}\nUser: ${selectedLogForTicket.user}\nChange: ${selectedLogForTicket.change}\nDescription: ${selectedLogForTicket.description}`,
          addedOn: new Date().toISOString(),
          assignedTo: '',
          lastUpdated: new Date().toISOString()
        } : null}
      />
    </>
  );
} 